import { useCallback, useMemo, useState } from 'react';

import { type AnyFieldApi, createFormHookContexts } from '@tanstack/react-form';

export const { fieldContext, useFieldContext, formContext, useFormContext } = createFormHookContexts();

/**
 * Clears onSubmit errors from a field's error map.
 * Called when the field value changes so that stale submit errors are removed.
 */
function clearSubmitErrors(fieldApi: AnyFieldApi): void {
    const { errorMap } = fieldApi.state.meta;
    if (errorMap.onSubmit) {
        fieldApi.setErrorMap({
            ...errorMap,
            onSubmit: undefined,
        });
    }
}

/**
 * Enhanced field context hook for use in field wrapper components.
 * Extends useFieldContext with:
 * - Automatic clearing of onSubmit errors when handleChange is called
 */
export function useAppFieldContext<TData>() {
    const fieldApi = useFieldContext<TData>();
    const [isFocused, setIsFocused] = useState(false);

    const clearErrors = useCallback(() => {
        clearSubmitErrors(fieldApi);
    }, [fieldApi]);

    const wrappedHandleChange = useCallback(
        (value: TData) => {
            clearSubmitErrors(fieldApi);
            fieldApi.handleChange(value);
        },
        [fieldApi],
    );
    const wrappedHandleBlur = useCallback(() => {
        setIsFocused(false);
        fieldApi.handleBlur();
    }, [fieldApi]);

    const wrappedHandleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    return useMemo(
        () =>
            Object.create(fieldApi, {
                handleChange: { value: wrappedHandleChange },
                clearSubmitErrors: { value: clearErrors },
                handleBlur: { value: wrappedHandleBlur },
                handleFocus: { value: wrappedHandleFocus },
                isFocused: { get: () => isFocused },
            }) as typeof fieldApi & { clearSubmitErrors: () => void; isFocused: boolean; handleFocus: () => void },
        [fieldApi, wrappedHandleChange, clearErrors, wrappedHandleBlur, wrappedHandleFocus, isFocused],
    );
}
